import { MessageBarType, Stack, Text } from '@fluentui/react';
import {
  BasicList,
  Button,
  buttonStylesLink,
  buttonStylesLinkBlack,
  FontSizes,
  IconName,
  Item,
  itemStylesTag,
  Loader,
  loaderStylesSpinnerXLarge,
  Pivot,
  useHaicPageTitle,
  useTheme,
  useToast,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  type App,
  type AppInstance,
  AppInstance_Status,
  AppInstance_Visibility,
  AppInstanceLifecycle,
  type LaunchProfiles,
} from '../../ai.h2o.cloud.appstore';
import { AppAttributeBadge } from '../../components/AppAttributeBadge/AppAttributeBadge';
import { AppDetailHeader } from '../../components/AppDetailHeader/AppDetailHeader';
import { AppPivot, AppPivotRightCard } from '../../components/AppPivot/AppPivot';
import { AppSocialInfo } from '../../components/AppSocialInfo/AppSocialInfo';
import { InstanceList } from '../../components/InstanceList/InstanceList';
import { InstanceSearchAndFilter } from '../../components/InstanceListPage/InstanceSearchAndFilter';
import { Markdown } from '../../components/Markdown/Markdown';
import { EnvService } from '../../services/api';
import { useApp, useEnv, useInstance, useUser } from '../../utils/hooks';
import { InstanceListType } from '../../utils/models';
import {
  getToastErrorMessage,
  handleErrMsg,
  InstancePauseResumeMap,
  InstancePauseResumeOpEnum,
} from '../../utils/utils';
import ErrorPage from '../ErrorPage';
import { useWorkspaces } from '../Orchestrator/WorkspaceProvider';
import { RoutePaths } from '../Routes';
import { appDetailPageStyles } from './appDetailPage.styles';
import { AdditionalInfo } from './components/AdditionalInfo/AdditionalInfo';
import { RunWithProfilePanel } from './components/RunWithProfilePanel/RunWithProfilePanel';
import { Share } from './components/Share/Share';
import { getActionButton as createActionButton } from './helpers/getActionButton';
import { useAppLocation } from './hooks/useAppLocation';
import { useAppDetailsData } from './useAppDetailsData';

type AppDetailPageProps = {
  id: string;
};

const toastGroupId = 'AppDetailPage';

export const windowOpenErrorMessage = `An error occurred when trying to automatically open a new tab to view the app.\
 This may be due to your browser settings, or a browser extension.\
 You may still view the new instance by clicking the "Visit" button.`;

enum TabKey {
  details = 'details',
  instances = 'instances',
  myInstances = 'myInstances',
}

function hasInstances(app: App) {
  return app.instanceLifecycle !== AppInstanceLifecycle.APP_INSTANCE_LIFECYCLE_LINK;
}

function AppDetailPage({ id }: AppDetailPageProps) {
  const {
    app,
    instances,
    myInstances,
    mainInstance,
    versions,
    fatalError,
    isInstancesLoading,
    pollersList,
    loadApp,
    loadInstances,
    runApp,
    setApp,
  } = useAppDetailsData(id);

  const theme = useTheme(),
    { ACTIVE_WORKSPACE_NAME, activeWorkspaceInitialized } = useWorkspaces(),
    { addToast } = useToast(),
    { updateLikes, updatePin } = useApp(),
    { setInstanceSuspension, terminateInstance } = useInstance(),
    env = useEnv(),
    user = useUser(),
    history = useHistory(),
    hasFullAccess = user?.hasFullAccess || false,
    publicMode = env?.publicModeEnabled && !user.hasFullAccess,
    isAdmin = user?.isAdmin,
    { appLocation } = useAppLocation(id),
    [launchProfiles, setLaunchProfiles] = useState<LaunchProfiles[] | undefined>(undefined),
    [refinedInstances, setRefinedInstances] = useState<AppInstance[]>(),
    [refinedMyInstances, setRefinedMyInstances] = useState<AppInstance[]>(),
    [loadingMessage, setLoadingMessage] = useState(''),
    [selectedTabKey, setSelectedTabKey] = useState<TabKey>(TabKey.details),
    [loading, setLoading] = useState<[string, boolean]>(),
    [hiddenAdvancedRunPanel, setHiddenAdvancedRunPanel] = useState(true),
    isManagedApp = app.instanceLifecycle === AppInstanceLifecycle.APP_INSTANCE_LIFECYCLE_MANAGED,
    isOwner = app.owner === user.name,
    isLinkApp = app.instanceLifecycle === AppInstanceLifecycle.APP_INSTANCE_LIFECYCLE_LINK,
    deployedInstanceLocation = mainInstance?.status === AppInstance_Status.DEPLOYED ? mainInstance?.location : null,
    runningAppLocation = isLinkApp ? app?.link?.location : appLocation || deployedInstanceLocation,
    loadProfiles = useCallback(async () => {
      const result = !hasFullAccess ? undefined : await EnvService.listLaunchProfiles({});
      setLaunchProfiles(result?.launchProfiles?.length ? result.launchProfiles : undefined);
    }, [hasFullAccess, EnvService.listLaunchProfiles]),
    loadData = useCallback(
      async (id?: string) => {
        if (!id) setLoadingMessage('Loading App');
        let app;
        try {
          [app] = await Promise.all([loadApp(), loadProfiles()]);
        } finally {
          if (!id) setLoadingMessage('');
        }
        if (app?.id && !publicMode) {
          void loadInstances(app, ACTIVE_WORKSPACE_NAME);
        }
      },
      [loadApp, loadInstances, ACTIVE_WORKSPACE_NAME]
    ),
    terminate = useCallback(
      (instance: AppInstance) => async () => {
        const { id } = instance;
        addToast({ message: `Terminating app instance with id ${id}`, messageBarType: MessageBarType.success });
        setLoading([id, true]);
        try {
          await terminateInstance(instance);
          await loadInstances(app);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unknown error';
          addToast(getToastErrorMessage(`Could not terminate app instance: ${handleErrMsg(message)}`, toastGroupId));
        } finally {
          setLoading([id, false]);
        }
      },
      [app, loadInstances, addToast, terminateInstance]
    ),
    setSuspension = useCallback(
      (appInstance: AppInstance, opEnum: InstancePauseResumeOpEnum) => async () => {
        const { id } = appInstance;
        const op = InstancePauseResumeMap.get(opEnum);
        if (!op) return;
        const { upperDescription } = op;
        addToast({ message: `${upperDescription} app instance with id ${id}`, messageBarType: MessageBarType.success });
        setLoading([id, true]);
        try {
          await setInstanceSuspension(appInstance, op);
          await loadData(id);
        } finally {
          setLoading([id, false]);
        }
      },
      [loadData, setInstanceSuspension]
    ),
    handleRunApp = useCallback(
      async (
        visibility: AppInstance_Visibility = AppInstance_Visibility.ALL_USERS,
        profile = '',
        runAsAdmin = false
      ) => {
        setLoading([id, true]);
        await runApp(visibility, profile, undefined, runAsAdmin);
        setLoading([id, false]);
      },
      [runApp]
    ),
    getActionButton = createActionButton({
      app,
      mainInstance: mainInstance || null,
      isAdmin,
      isOwner,
      isManagedApp,
      launchProfiles,
      publicMode: publicMode || false,
      isInstancesLoading,
      appLocation: runningAppLocation,
      handleRunApp,
      runApp,
      setSuspension,
      setHiddenAdvancedRunPanel,
    }),
    getDetailsContent = () => (
      <AppPivot
        mainContent={
          app.longDescription ? (
            <Markdown source={app.longDescription} />
          ) : (
            <p style={{ fontStyle: `italic` }}>This application has no description</p>
          )
        }
        images={app.screenshotLocations}
        rightContent={
          <>
            <Share app={app} />
            <AppPivotRightCard>
              <AdditionalInfo app={app} />
            </AppPivotRightCard>
          </>
        }
      />
    ),
    getTabs = () => {
      const detailsTab = {
          key: TabKey.details,
          headerText: 'App Details',
          content: !loadingMessage && getDetailsContent(),
        },
        myInstancesTab = {
          key: TabKey.myInstances,
          headerText: `My Instances ${myInstances?.length || '0'}`,
          loading: isInstancesLoading,
          content: (
            <Stack styles={{ root: { width: '100%', paddingBottom: 15 } }}>
              <Stack style={{ marginTop: 20 }}>
                <InstanceSearchAndFilter instances={myInstances} onRefine={setRefinedMyInstances} />
                <InstanceList
                  instances={refinedMyInstances}
                  type={InstanceListType.app}
                  loadingMsg={loadingMessage}
                  loadingInstance={loading}
                  hideInstanceLogButton
                  terminateInstance={terminate}
                  setInstanceSuspension={setSuspension}
                />
              </Stack>
            </Stack>
          ),
        },
        instancesTab = {
          key: TabKey.instances,
          headerText: `Workspace Instances ${instances?.length || '0'}`,
          loading: isInstancesLoading,
          content: (
            <Stack styles={{ root: { width: '100%', paddingBottom: 15 } }}>
              {!isInstancesLoading && instances && (
                <Stack style={{ marginTop: 20 }}>
                  <InstanceSearchAndFilter instances={instances} onRefine={setRefinedInstances} />
                  <InstanceList
                    instances={refinedInstances}
                    type={InstanceListType.app}
                    loadingMsg={loadingMessage}
                    loadingInstance={loading}
                    hideInstanceLogButton
                    terminateInstance={terminate}
                    setInstanceSuspension={setSuspension}
                  />
                </Stack>
              )}
            </Stack>
          ),
        };
      return hasInstances(app) ? [detailsTab, myInstancesTab, instancesTab] : [detailsTab];
    },
    onLike = useCallback(async () => {
      try {
        const resApp = await updateLikes(app);
        if (resApp) setApp(resApp);
      } finally {
        setLoadingMessage('');
      }
    }, [app, updateLikes]),
    onPin = useCallback(async () => {
      try {
        const resApp = await updatePin(app);
        if (resApp) setApp(resApp);
      } finally {
        setLoadingMessage('');
      }
    }, [app, updatePin]),
    getFooterForHeader = () => {
      return (
        <>
          <Text
            styles={{
              root: {
                color: theme.palette?.gray500,
                fontSize: FontSizes.textPrimary,
                paddingTop: 8,
              },
            }}
          >
            {app?.owner}
          </Text>
          <AppSocialInfo {...app.preference} onLike={onLike} onPin={onPin} publicMode={publicMode} />
          <AppAttributeBadge app={app} />
          <BasicList
            id="app-tags"
            horizontal
            data={app.tags?.map((tag) => ({
              ...tag,
              style: { borderLeft: `4px solid ${tag.color}` },
            }))}
            filterFn={(tag) => !tag.isCategory && !tag.hidden}
            itemRenderer={(d) => (
              <Item
                styles={itemStylesTag}
                labelField="title"
                titleField="description"
                hasTooltip
                styleField="style"
                emptyTooltip="No description available."
                data={d}
              />
            )}
          />
        </>
      );
    };

  useEffect(() => {
    if (activeWorkspaceInitialized) void loadData();
  }, [loadData, activeWorkspaceInitialized]);

  useEffect(() => {
    return () => {
      for (const poller of pollersList) {
        poller.suspend();
      }
    };
  }, [pollersList]);

  useHaicPageTitle(app.title, env?.cloudInstanceName);

  return fatalError ? (
    <Stack styles={{ root: { margin: 16 } }}>
      <ErrorPage {...fatalError} />
    </Stack>
  ) : loadingMessage ? (
    <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
  ) : (
    <Stack styles={appDetailPageStyles()}>
      <Button
        styles={[buttonStylesLink, buttonStylesLinkBlack]}
        text="Back"
        iconName={IconName.Back}
        onClick={() => (window.history.state ? history.goBack() : history.push(RoutePaths.APPSTORE))}
      />
      <Stack tokens={{ childrenGap: `1.5rem` }}>
        <AppDetailHeader
          actionButton={getActionButton(loading)}
          app={app}
          footer={getFooterForHeader()}
          description={app.description}
          iconLocation={app.iconLocation}
          tags={app.tags}
          title={app.title}
          versions={versions}
        />
        {publicMode ? (
          getDetailsContent()
        ) : (
          <div className="app-detail-content">
            <Pivot
              selectedKey={selectedTabKey}
              items={getTabs()}
              onLinkClick={(item) => {
                if (item) {
                  setSelectedTabKey(item.props.itemKey as TabKey);
                }
              }}
            />
          </div>
        )}
      </Stack>
      {!hiddenAdvancedRunPanel && (
        <RunWithProfilePanel
          app={app}
          profiles={launchProfiles || []}
          onDismiss={() => setHiddenAdvancedRunPanel(true)}
          onRun={async ({ visibility, profile }, runAsAdmin) => {
            void handleRunApp(visibility, profile, runAsAdmin);
            setHiddenAdvancedRunPanel(true);
          }}
          isAdmin={isAdmin}
          onlyAdmin={isAdmin && !isOwner && isManagedApp}
        />
      )}
    </Stack>
  );
}

export default AppDetailPage;
